import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { throwError, timer } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';
import { DinnoHttpError } from '@core/models/dinno-http-error.model';

import { logger } from '@core/utils/logger';
export const httpResilienceInterceptor: HttpInterceptorFn = (req, next) => {
  const READ_TIMEOUT_MS = 12_000;
  const MAX_RETRIES = 2;
  const BASE_DELAY_MS = 600;

  if (req.url.includes('/events-subscriptions/')) {
    return next(req);
  }

  const method = req.method.toUpperCase();
  const isRead = method === 'GET' || method === 'HEAD';

  const isRetryable = (err: unknown) => {
    if (err instanceof Error && err.name === 'TimeoutError') return true;
    if (err instanceof HttpErrorResponse) {
      return err.status === 0 || [502, 503, 504].includes(err.status);
    }
    return false;
  };

  const backoff = (attempt: number) => BASE_DELAY_MS * Math.pow(2, attempt - 1);

  const normalize = (err: unknown): DinnoHttpError => {
    if (err instanceof Error && err.name === 'TimeoutError') {
      return new DinnoHttpError({
        kind: 'timeout',
        title: 'Servidor lento',
        message: 'El servidor no respondió a tiempo. Probá reintentar.',
        retryable: true,
        cause: err,
      });
    }

    if (err instanceof HttpErrorResponse) {
      const status = err.status;
      // El back manda el id de su traza en el header (expuesto por CORS) y en el cuerpo del error
      const traceId: string | undefined =
        err.headers?.get('X-Trace-Id') ?? err.error?.traceId ?? undefined;

      if (status === 0) {
        return new DinnoHttpError({
          kind: 'network',
          title: 'No se pudo conectar',
          message: 'No se pudo contactar con el servidor. Reintentá.',
          retryable: true,
          status,
          traceId,
          cause: err,
        });
      }

      if ([502, 503, 504].includes(status)) {
        return new DinnoHttpError({
          kind: 'server',
          title: 'Servidor no disponible',
          message: 'El servicio está con demoras o caído. Reintentá en unos segundos.',
          retryable: true,
          status,
          traceId,
          cause: err,
        });
      }

      if (status === 401) {
        return new DinnoHttpError({
          kind: 'auth',
          title: err.error?.message ? 'Credenciales inválidas' : 'Sesión vencida',
          message: err.error?.message ?? 'Tu sesión venció. Volvé a iniciar sesión.',
          retryable: false,
          status,
          appCode: err.error?.appCode,
          traceId,
          cause: err,
        });
      }

      const appCode = err.error?.appCode;

      return new DinnoHttpError({
        kind: 'http',
        title: 'Ocurrió un error',
        message: err.error?.message ?? `Error ${status}.`,
        retryable: false,
        status,
        appCode,
        traceId,
        cause: err,
      });
    }

    return new DinnoHttpError({
      kind: 'unknown',
      title: 'Error inesperado',
      message: 'Ocurrió un error inesperado. Probá reintentar.',
      retryable: true,
      cause: err,
    });
  };

  let stream$ = next(req);

  if (isRead) {
    stream$ = stream$.pipe(timeout(READ_TIMEOUT_MS));
  }

  stream$ = stream$.pipe(catchError((err) => throwError(() => normalize(err))));

  if (isRead) {
    stream$ = stream$.pipe(
      retry({
        count: MAX_RETRIES,
        delay: (error, retryCount) => {
          const originalError = error instanceof DinnoHttpError ? error.cause : error;

          if (!isRetryable(originalError) || retryCount > MAX_RETRIES) {
            return throwError(() => error);
          }

          logger.warn('HTTP', `Reintentando la petición: intento=${retryCount}`);
          return timer(backoff(retryCount));
        },
      }),
    );
  }

  return stream$;
};
